import {
  type EditRegionItem,
  resolveEditRegions,
  toEditRegionItems
} from "@frontend/pages/case-workspace/pathology/lib/module-edit-regions"
import type { SeriesEditBbox } from "@frontend/pages/case-workspace/pathology/types"
import type { TileViewer } from "@virdx/wsi-viewer"
import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"

const OUTSIDE_REGION_TOAST_ID = "edit-region-outside"

interface UseEditRegionsParams {
  viewerRef: RefObject<TileViewer | null>
  viewerEpoch: number
  /**
   * Edit-region domain of the active module (`getModuleEditRegionDomain`); null =
   * the module never uses edit regions and the viewer is unrestricted.
   */
  domain: string | null
  /**
   * Edit bounding boxes of the active base series (all domains). `null` = the
   * slide has none; `undefined` = not known yet (request pending / failed).
   */
  editBboxes: SeriesEditBbox[] | null | undefined
}

export interface EditRegionsState {
  /** Regions confining the active module (null = unrestricted). */
  regions: EditRegionItem[] | null
  /** The regions' dim / outlines are hidden in the viewer (editing stays confined). */
  hidden: boolean
  setHidden: (hidden: boolean) => void
  jumpToRegion: (key: string) => void
  nextRegion: () => void
}

/**
 * Single owner of the viewer's edit regions. Resolves which of the slide's
 * edit bounding boxes confine the active module (label_raster always, vector
 * when it opts in), pushes them to the viewer, and toasts when a press lands
 * outside them. Switching modules switches the regions; a module without a
 * domain or a slide without matching boxes is unrestricted. While the boxes
 * are not known yet the caller keeps editing tools off (fail closed).
 */
export function useEditRegions({ viewerRef, viewerEpoch, domain, editBboxes }: UseEditRegionsParams): EditRegionsState {
  // Visual preference only: hide the edit regions' dim / outlines (editing stays confined).
  const [hidden, setHidden] = useState(false)

  // Stable identity: a fresh array per render would re-run the viewer effect below.
  const regionsKey = JSON.stringify(resolveEditRegions(editBboxes, domain))
  const regions = useMemo(() => JSON.parse(regionsKey) as SeriesEditBbox[] | null, [regionsKey])
  const regionItems = useMemo(() => (regions ? toEditRegionItems(regions) : null), [regions])

  // Regions → viewer: confine editing and dim the rest while the module is active.
  useEffect(() => {
    const viewer = viewerRef.current
    if (!viewer || viewerEpoch === 0) return
    viewer.setEditRegions(
      regions
        ? regions.map((bbox) => ({
            id: bbox.roi_key,
            left: bbox.x,
            top: bbox.y,
            right: bbox.x + bbox.width,
            bottom: bbox.y + bbox.height
          }))
        : null
    )
  }, [viewerEpoch, regions, viewerRef])

  // Hidden → viewer: a render-time switch, so the confinement above is unaffected.
  useEffect(() => {
    const viewer = viewerRef.current
    if (!viewer || viewerEpoch === 0) return
    viewer.setEditRegionsHidden(hidden)
  }, [viewerEpoch, hidden, viewerRef])

  // Presses outside the regions (label raster brush / fill, vector draw / SAM).
  useEffect(() => {
    const viewer = viewerRef.current
    if (!viewer || viewerEpoch === 0) return
    const onOutside = () => {
      // Fixed id: repeated presses replace the hint instead of stacking toasts.
      toast.info("Editing is limited to the marked regions", {
        id: OUTSIDE_REGION_TOAST_ID,
        description: "Only the bright rectangles can be edited on this slide."
      })
    }
    viewer.on("label-raster-outside-region", onOutside)
    viewer.on("annotation-outside-region", onOutside)
    return () => {
      viewer.off("label-raster-outside-region", onOutside)
      viewer.off("annotation-outside-region", onOutside)
    }
  }, [viewerEpoch, viewerRef])

  const lastJumpedRegionRef = useRef<string | null>(null)
  const jumpToRegion = useCallback(
    (key: string) => {
      const viewer = viewerRef.current
      const bbox = regions?.find((r) => r.roi_key === key)
      if (!(viewer && bbox)) return
      lastJumpedRegionRef.current = key
      viewer.fitToRect(bbox.x, bbox.y, bbox.x + bbox.width, bbox.y + bbox.height)
    },
    [viewerRef, regions]
  )
  const nextRegion = useCallback(() => {
    if (!regions || regions.length === 0) return
    const currentIndex = regions.findIndex((r) => r.roi_key === lastJumpedRegionRef.current)
    const next = regions[(currentIndex + 1) % regions.length]
    if (next) jumpToRegion(next.roi_key)
  }, [regions, jumpToRegion])

  return { regions: regionItems, hidden, setHidden, jumpToRegion, nextRegion }
}
